import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type {
  AgencyPublicationModel, AgencyPublicationRequestOptions,
} from '@models/types'
import type { ModuleState } from '@redux/modules/types'
import { EntitiesState } from '@redux/entities'

const watchEntityKeys = ['agencyPublications', 'publications']

type HandleSort = {
  agencies: EntitiesState['agencies'],
  agencyPublication: AgencyPublicationModel,
  publications: EntitiesState['publications'],
  sortKey: string,
}

const handleSort = (params: HandleSort) => {
  const {
    agencyPublication, sortKey, agencies, publications,
  } = params
  if (sortKey === 'publication_id'){
    return publications[agencyPublication.publication_id]?.title
  }

  return agencies[agencyPublication.agency_id]?.title
}

type AgencyPublicationFilters = {
  agency_id?: number,
  agency_string?: string,
  publication_id?: number,
  publication_string?: string,
}

type UseAgencyPublicationsOptions = {
  entityKey?: string,
  filters?: AgencyPublicationFilters,
  performHttpRequests?: boolean,
  requestOptions?: AgencyPublicationRequestOptions,
  sortKey?: string,
}

function useAgencyPublications(options: UseAgencyPublicationsOptions) {
  const {
    entityKey, filters, requestOptions = {}, sortKey = 'agency_id',
  } = options
  const {
    agency_id: filterAgencyId,
    agency_string: filterAgencyString,
    publication_id: filterPublicationId,
    publication_string: filterPublicationString,
  } = filters || {}

  const {
    updatedEntities: {
      agencyPublications: agencyPublicationsUpdatedAt,
      publications: publicationsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const {
    agencies,
    agencyPublications,
    publications,
  } = useSelector(reduxState => reduxState.entities)

  const filteredAgencyPublications = useMemo(() => {
    const filtered = Object.values(agencyPublications).filter((agencyPublication) => {
      const { agency_id, publication_id } = agencyPublication
      const { title: agencyTitle } = agencies[agency_id] || {}
      const { title: publicationTitle } = publications[publication_id] || {}

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)
      const matchPublicationId = matchFilterNumber(publication_id, filterPublicationId)

      const matchAgencyStringId = agency_id?.toString() === filterAgencyString
      const matchAgencyTitle = matchFilterString(agencyTitle, filterAgencyString)
      const matchAgencyString = matchAgencyStringId || matchAgencyTitle

      const matchPublicationStringId = publication_id?.toString() === filterPublicationString
      const matchPublicationTitle = matchFilterString(publicationTitle, filterPublicationString)
      const matchPublicationString = matchPublicationStringId || matchPublicationTitle

      return matchAgencyId && matchAgencyString && matchPublicationId && matchPublicationString
    })

    return sortArrayBy(
      filtered,
      'asc',
      agencyPublication => handleSort({
        agencies, agencyPublication, publications, sortKey,
      }),
    )
  }, [agencyPublicationsUpdatedAt, publicationsUpdatedAt, JSON.stringify(filters)])

  const filteredAgencyPublicationsCount = filteredAgencyPublications.length
  const hasFilteredAgencyPublications = !!filteredAgencyPublicationsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAgencyPublicationsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAgencyPublications } = useReduxAction(
    'agencyPublications',
    'loadAgencyPublications',
    {
      entityKey,
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loadedForKeys, loading }: ModuleState) => {
        if (entityKey) return performHttpRequests && !loadedForKeys?.includes(entityKey) && !loading
        return performHttpRequests && !loading
      },
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyPublications,
    filteredAgencyPublicationsCount,
    hasFilteredAgencyPublications,
    loading: loadingAgencyPublications,
  }
}

export default useAgencyPublications
